/** Canonical pure semantics, shared by the host and native editor. */
import { type SceneGenSpec } from './scene-gen-spec-semantics';
import { isSubmittableSeed } from '../video-seed';


/** 재생성 노드에서 캡슐이 손대는 필드만 추린 조각. */
export interface SceneSpecRegenFields {
  prompt: string;
  extraRequest?: string;
  durationSec?: number;
  /**
   * 🌬 환경음 글 묘사(W4 F13) — 노드에 **이미 있는 축**이라 캡슐이 매니페스트 값을 이긴다.
   * 조립부(`AgentStudio` 재생성 노드)가 매니페스트 전개 **뒤에** 이 조각을 펴므로 승패가 여기서 정해진다.
   *
   * ★`null` = **명시 제거**★(세호 QA W-C F13 · 3상태) — "이 컷은 환경음 없이 굽는다"는 지시다.
   *   빈 문자열·`undefined`(=캡슐이 말하지 않음)와 **다른 사실**이라 여기서 접으면 안 된다:
   *   전자는 매니페스트를 이겨 소리를 없애고, 후자는 매니페스트를 그대로 둔다.
   * ★노드로 접는 것은 조립부가 아니라 {@link resolveRegenAmbientSound} 다★ — `SceneNode.ambientSound`
   *   는 여전히 2상태(문자열/부재)이므로, 3번째 상태는 이 조각까지만 살고 거기서 "키 없음"이 된다.
   */
  ambientSound?: string | null;
  /**
   * 🔔 효과음 글 메모(W4 F13) — 노드 **신설 축**(`SceneNode.sfxNote`).
   * ★`sfx`(컷 자신의 효과음 묘사)·`sfxUrl`/`sfxManual`(유저가 고른 음원 파일)은 건드리지 않는다★ —
   *   생성 프롬프트에서 `sfx` 와 같은 절에 **합류**할 뿐, 어느 쪽도 덮지 않는다.
   */
  sfxNote?: string;
  /**
   * 🧬 **일관성 축 승계**(PR-1b · 결정 D) — 캡슐이 말했을 때만 실린다.
   * ★노드 필드가 아니다★ — 조립부는 이 값을 노드에 펴지 않고 ★제출 옵션★(seed)과 ★기록★(연쇄 추적)
   *   으로만 쓴다. 노드에 얹으면 EF 가 모르는 키가 요청 조립을 타고 흘러들고, 저장분에도 남는다.
   */
  consistency?: { seed?: number; anchorUrl?: string };
}


/**
 * 🎯 시작 앵커를 **어떻게 조달할 것인가** — 화면이 이 값을 보고 프레임을 뽑거나 말거나 한다.
 *  · `prev-end`  = 앞 씬 끝 프레임을 새로 추출해 앵커로(연속)
 *  · `cast-main` = **캐스트 메인샷**(W3 F07) — `startAnchor.castName` 이 가리키는 멤버의 기준
 *                  이미지를 `cast[]` 에서 ★그 순간★ 조회해 앵커로. 조회 미스는 `none` 과 같이 흘린다.
 *  · `none`      = 앵커 없음(전환) — ★명시적으로 비운다★. `keep` 과 다르다.
 *  · `keep`      = 종전 동작(매니페스트의 startImageUrl 그대로) — 캡슐이 이 축을 말하지 않았을 때.
 */
export type SceneSpecAnchorMode = "prev-end" | "cast-main" | "none" | "keep";


/**
 * 캡슐이 요구하는 앵커 조달 방식 — **입력은 `startAnchor` 하나뿐이다**(결정 C 단일 축).
 *
 * ★舊 `transition`(연속/전환)은 읽지 않는다★(세호님 F01 확인 판정 2026-08-18): 두 축을 함께 보면
 *   둘이 어긋난 저장분에서 어느 쪽이 이기는지가 판정마다 갈리고, 그 갈림이 곧 `연속 + 캐릭터
 *   메인 샷` 같은 모순 조합의 실행 경로다. 의미는 옵션으로 승계됐다 — 舊연속 = `prev-end`,
 *   舊전환 = `none`(지정 안 함 · 새 장면).
 * ★`character-main` 은 W3 에서 열렸다★ — **이름이 실렸을 때만** `cast-main` 으로 조달을 지시한다.
 *   조달 원천은 캡슐이 아니라 `cast[]` 라, 이 순수 함수는 "어디서 가져올지"만 말하고 실제 조회는
 *   {@link resolveCastMainAnchor} 가 한다(판정과 조달을 섞지 않는다).
 * ★이름 없는 `character-main` 은 `keep` 이다★(리드 결정 · 적대 1R) — 그건 "메인샷으로 시작하겠다,
 *   다만 인물은 아직 안 골랐다"는 **중간 상태**이지 "앵커를 비워라"가 아니다. `none` 으로 접으면
 *   유저가 셀렉트를 고른 순간 매니페스트의 시작 화면이 사라져 W2 거동이 뒤바뀌고, `cast-main` 으로
 *   보내면 조달할 이름이 없어 매번 미스 고지가 뜬다. 저장 형상은 남기고 거동만 종전으로 둔다.
 * ★`upload` 는 여전히 `keep` 이다★(P2) — 그 원천(캡슐 url)을 쓰는 코드가 없는데 `none` 으로 접으면
 *   "시작 기준을 골랐더니 오히려 앵커가 사라지는" 반대 동작이 된다. 지원하지 않는 값은 **종전
 *   동작으로 우아 저하**하고, 화면이 지원 여부를 말한다(조용한 무시 금지).
 */
export function sceneSpecAnchorMode(spec: SceneGenSpec | null | undefined): SceneSpecAnchorMode {
  const kind = spec?.startAnchor?.kind;
  if (kind === "prev-end") return "prev-end";
  if (kind === "none") return "none";
  if (kind === "character-main") return spec?.startAnchor?.castName?.trim() ? "cast-main" : "keep";
  if (kind === "upload") return "keep";
  return "keep"; // 미설정·어휘 밖. ★舊 `transition` 폴백은 여기에 없다★(결정 C — 입력은 startAnchor 하나)
}


/* ── 🖊 캡슐 텍스트 축의 **문면 SSOT**(수희 C절 · 이미지 씬 캡슐 소비) ─────────────────────────
 *   영상 재생성({@link applySceneSpecToRegenFields})과 이미지 배치({@link sceneSpecImagePromptAddendum})가
 *   ★같은 한 벌★을 쓴다 — 두 벌이 되면 같은 저장값이 창구(영상/이미지)에 따라 다른 문면으로 실려,
 *   "시트에서 저장한 지시"의 재현성이 창구별로 갈린다(캡슐의 존재 이유가 깨진다). */
export const sceneDirectionClause = (action: string): string =>
  `[Scene direction — saved setting for this cut]\n${action}`;

export const savedRequirementClause = (extraRequest: string): string =>
  `Saved requirement for this cut (applies to every take): ${extraRequest}`;


/**
 * 🧬 **캡슐을 재생성 노드 필드에 얹는다**(캡슐 승).
 *
 * · `action`(상세 설명)은 **프롬프트 본문**을 보강한다 — 기타 요청 슬롯에 넣으면 "추가 요청"이라는
 *   약한 층에 실려 원 연출에 밀린다. 이건 이 컷이 무엇을 보여주는지 자체다.
 * · `extraRequest`(기타 요청)는 **영구분**이라 1회성 유저 피드백과 **공존**한다 — 둘 중 하나를
 *   덮어쓰면 "채팅으로 지적한 것"이나 "시트에 저장한 것" 중 하나가 매번 증발한다. 순서는
 *   1회성 → 영구: 이번 회차의 지적이 앞서고, 상시 제약이 뒤에서 못을 박는다.
 * · `durationSec` 은 캡슐이 이긴다. ★`lockDuration` 은 켜지 않는다★ — 켜면 음성 동기화가 꺼져
 *   8초 발화가 5초 컷에서 잘린다. 시트가 이미 음성 하한 아래를 못 고르게 막고 있고, 그래도 남는
 *   경계는 생성 직전 `resolveCutDuration` 이 올려 잡는 편이 안전하다(잘림 < 약간 긴 컷).
 */
export function applySceneSpecToRegenFields(
  fields: SceneSpecRegenFields,
  spec: SceneGenSpec | null | undefined,
): SceneSpecRegenFields {
  if (!spec) return fields;
  const out: SceneSpecRegenFields = { ...fields };

  if (spec.action) {
    /* 🏭 제작 출처(`actionSource:"production"`)의 `action` 은 인계 시드값이라 **이미 제작 프롬프트에 녹아 있다**
       (QA 2026-09-06) — 그대로면 지시 절을 붙이지 않는다(붙이면 같은 연출이 두 번 실리고 «설정이 그대로라
       거의 같은 영상» 고지 키도 어긋난다). 유저가 고치면 저장 관문이 표식을 지워 종전대로 실린다.
       ★바깥 `if (spec.action) {` 줄은 바이트 그대로★ — 형제 뮤테이션 스위트(expert-port-w2-spec-regen-merge 변조④)의 앵커다. */
    if (spec.actionSource !== "production") {
      // 🖊 문면은 위 SSOT 헬퍼 — 이미지 배치(sceneSpecImagePromptAddendum)와 같은 한 벌(바이트 동일).
      out.prompt = `${fields.prompt ?? ""}\n\n${sceneDirectionClause(spec.action)}`.trim();
    }
  }
  /* 🏙 **장소 지시 절은 ★이 함수가 붙이지 않는다★**(2R P1-1 · grep ⓐ 폐쇄) — 판정에 필요한
     `shippedLocationRef`(조립 결과)를 이 함수는 알 수 없고, 알 수 있는 척하면 두 소비자가 갈린다:
       ①발주부(`runSceneRegen`)는 참조 조립을 끝낸 뒤라 탑재 여부를 안다.
       ②재생성 카드 창구는 조립 **전**이라 모른다 — 그런데 두 창구는 이 함수 산출로 같은
         `syntheticDirectiveKey` 를 지어 대조한다(*"거의 같은 영상"* 고지의 성립 조건).
     ⇒ 여기에 장소 절을 넣으면 카드 쪽 키에만 절이 빠져 **양쪽 키가 영구히 갈리고** 그 고지가
       영원히 침묵한다. 절 부착은 조립부가 소유하고(`sceneSpecLocationDirective`), 이 함수의
       산출은 **두 창구가 재현 가능한 값**으로만 남는다. */
  if (spec.extraRequest) {
    const prior = (fields.extraRequest ?? "").trim();
    // 🖊 문면은 위 SSOT 헬퍼 — 이미지 배치와 같은 한 벌(바이트 동일).
    const saved = savedRequirementClause(spec.extraRequest);
    out.extraRequest = prior ? `${prior}\n${saved}` : saved;
  }
  if (typeof spec.durationSec === "number" && spec.durationSec > 0) {
    out.durationSec = spec.durationSec;
  }
  /* 🔊 소리 두 축(W4 F13) — **캡슐이 말했을 때만** 싣는다. 빈 값으로 덮으면 매니페스트가 기억하는
     제작 당시 소리가 "시트를 열어만 본" 씬에서 사라진다(부재 = 종전 동작이라는 이 레인의 계약).
     ★환경음만 판정이 truthy 가 아니라 **presence** 다★(세호 QA W-C F13): `null`(명시 제거)은 truthy
       가 아니지만 **캡슐이 한 말**이라 반드시 실려야 한다. 종전 `if (spec.ambientSound)` 는 그 말을
       "안 했다"로 읽어, 유저가 [환경음 없음]을 켜도 매니페스트 소리가 그대로 다시 실렸다.
     ★`""` 는 여전히 "안 말했다"다★ — 저장 관문이 만들 수 없는 형상(손편집·구 저장분)이고, 그것을
       제거로 읽으면 낡은 데이터가 조용히 소리를 지운다. 제거의 어휘는 `null` 하나뿐이다. */
  if (spec.ambientSound === null) out.ambientSound = null;
  else if (spec.ambientSound) out.ambientSound = spec.ambientSound;
  if (spec.sfxNote) out.sfxNote = spec.sfxNote;
  /* 🧬 일관성 축(PR-1b · 결정 D) — **캡슐이 말했을 때만** 싣는다(소리 두 축과 같은 계약).
     ★빈 객체는 만들지 않는다★: `{}` 를 얹으면 호출부의 "캡슐이 일관성을 말했나" 판정이 참이 되어,
     아무것도 지정하지 않은 씬이 승계 우선순위에서 직전 스탬프를 이겨 버린다(부재를 지시로 오독). */
  {
    const seed = isSubmittableSeed(spec.consistency?.seed) ? spec.consistency.seed : undefined;
    const anchorUrl = typeof spec.consistency?.anchorUrl === "string" && spec.consistency.anchorUrl.trim()
      ? spec.consistency.anchorUrl.trim()
      : undefined;
    if (seed !== undefined || anchorUrl !== undefined) {
      out.consistency = { ...(seed !== undefined ? { seed } : {}), ...(anchorUrl ? { anchorUrl } : {}) };
    }
  }
  return out;
}
